import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Bars3Icon,
    XMarkIcon,
    HomeIcon,
    UsersIcon,
    UserIcon,
    ChartBarIcon,
    ArrowRightOnRectangleIcon,
    ArrowLeftStartOnRectangleIcon,
    ArrowRightStartOnRectangleIcon,
    QuestionMarkCircleIcon,
    SparklesIcon,
    AdjustmentsHorizontalIcon,
    BriefcaseIcon,
    ChartPieIcon,
    ArrowPathIcon,
    PhotoIcon,
    BookOpenIcon,
    CubeTransparentIcon,
    TrophyIcon,
    ChevronDownIcon,
    BuildingOffice2Icon,
    IdentificationIcon,
    FlagIcon,
    ChatBubbleLeftRightIcon,
    Cog6ToothIcon,
    EyeIcon,
    BellAlertIcon,
    BellIcon,
    PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { employeeCanAccessPath, PanelAccess } from '../../auth/adminAccess';

interface NavItem {
    name: string;
    href: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    disabled: boolean;
}

const navigation: NavItem[] = [
    { name: 'Dashboard',              href: '/dashboard',          icon: HomeIcon,                 disabled: false },
    { name: 'The Wizard',             href: '/wizard',             icon: SparklesIcon,             disabled: false },
    { name: 'Wellness Q&As',          href: '/wellness-qa',        icon: QuestionMarkCircleIcon,   disabled: false },
    // { name: 'Session Load Modifiers', href: '/session-lms',        icon: AdjustmentsHorizontalIcon, disabled: false },
    { name: 'Training Types & Sports', href: '/training-types-sports', icon: TrophyIcon,           disabled: false },
    { name: 'Global Activities',      href: '/global-activities',  icon: CubeTransparentIcon,      disabled: false },
    { name: 'Alert Rules',            href: '/alert-rules',        icon: BellAlertIcon,             disabled: false },
    { name: 'Push Notifications',     href: '/push-notifications', icon: BellIcon,                  disabled: false },
    { name: 'Campaigns',              href: '/campaigns',          icon: ChartPieIcon,             disabled: false },
    { name: 'Cancellation Feed.', href: '/cancellation-feedback', icon: ChatBubbleLeftRightIcon, disabled: false },
    { name: 'Reporting',          href: '/reporting',            icon: FlagIcon,               disabled: false },
    { name: 'Social Monitoring',  href: '/social-monitoring',    icon: EyeIcon,                disabled: false },
    { name: 'Cancellation Req.',     href: '/deletion-requests',    icon: ChartBarIcon,           disabled: false },
    { name: 'Background Gallery',     href: '/background-gallery', icon: PhotoIcon,                disabled: false },
    { name: 'Peak Global Settings',   href: '/peak-global-settings', icon: Cog6ToothIcon,          disabled: false },
];

const analyticsItems: NavItem[] = [
    { name: 'Analytics',           href: '/analytics',            icon: ChartBarIcon,              disabled: false },
    { name: 'Reporting Analytics', href: '/reporting-analytics',  icon: PresentationChartLineIcon, disabled: false },
];

const userItems: NavItem[] = [
    { name: 'Users',          href: '/users',          icon: UsersIcon,      disabled: false },
    { name: 'User Analytics', href: '/user-analytics', icon: ChartBarIcon,   disabled: false },
];

const commercialItems: NavItem[] = [
    { name: 'Organisations',       href: '/commercial/organisations',icon: BuildingOffice2Icon,     disabled: false },
    { name: 'CRM',                    href: '/crm',                icon: BriefcaseIcon,            disabled: false },
];

const employeesItem: NavItem = { name: 'Employees', href: '/hr/employees', icon: IdentificationIcon, disabled: false };

let permissionsCache: { uid: string; access: PanelAccess } | null = null;

const DashboardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [analyticsOpen, setAnalyticsOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const [commercialOpen, setCommercialOpen] = useState(false);
    const [access, setAccess] = useState<PanelAccess>(permissionsCache?.access ?? {});
    const [permissionsLoaded, setPermissionsLoaded] = useState(permissionsCache !== null);

    const location = useLocation();
    const navigate = useNavigate();
    const { logout, currentUser } = useAuth();

    useEffect(() => {
        if (!currentUser) return;
        if (permissionsCache?.uid === currentUser.uid) return;
        getDoc(doc(db, 'employees', currentUser.uid)).then((snap) => {
            const data = snap.data() ?? {};
            const next: PanelAccess = {
                status: typeof data.status === 'string' ? data.status : undefined,
                enabled: typeof data.enabled === 'boolean' ? data.enabled : undefined,
                permissions: Array.isArray(data.permissions) ? data.permissions : [],
                superAdmin: data.superAdmin === true,
            };
            permissionsCache = { uid: currentUser.uid, access: next };
            setAccess(next);
            setPermissionsLoaded(true);
        }).catch(() => setPermissionsLoaded(true));
    }, [currentUser]);

    const canSee = (href: string) => permissionsLoaded && employeeCanAccessPath(access, href);

    const visibleNav = navigation.filter((item) => canSee(item.href));
    const visibleAnalyticsItems = analyticsItems.filter((item) => canSee(item.href));
    const visibleUserItems = userItems.filter((item) => canSee(item.href));
    const visibleCommercialItems = commercialItems.filter((item) => canSee(item.href));

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Failed to logout:', error);
            window.alert('Sign out failed. Close this browser window to end the session, then try again.');
        }
    };

    const isActive = (path: string) => location.pathname === path;

    const analyticsDropdown = (mobile = false) => {
        if (visibleAnalyticsItems.length === 0) return null;
        const anyActive = visibleAnalyticsItems.some((i) => isActive(i.href));
        return (
            <div>
                <button
                    onClick={() => setAnalyticsOpen((o) => !o)}
                    className={`flex items-center gap-3 rounded-lg px-2 w-full transition-colors text-sm ${
                        mobile ? 'h-10' : 'py-2'
                    } ${sidebarCollapsed && !mobile ? 'justify-center' : ''} ${
                        anyActive ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
                    }`}
                    title={sidebarCollapsed && !mobile ? 'Analytics' : undefined}
                >
                    <ChartBarIcon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                    {(mobile || !sidebarCollapsed) && (
                        <>
                            <span className="flex-1 text-left">Analytics</span>
                            <ChevronDownIcon
                                className={`h-3 w-3 flex-shrink-0 transition-transform ${analyticsOpen ? 'rotate-180' : ''}`}
                            />
                        </>
                    )}
                </button>
                {analyticsOpen && (mobile || !sidebarCollapsed) && (
                    <div className="ml-4 mt-0.5 flex flex-col gap-0.5">
                        {visibleAnalyticsItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={mobile ? () => setSidebarOpen(false) : undefined}
                                className={`flex items-center gap-3 rounded-lg px-2 transition-colors text-sm ${
                                    mobile ? 'h-10' : 'py-2'
                                } ${
                                    isActive(item.href) ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
                                } ${item.disabled ? 'text-gray-400 cursor-not-allowed pointer-events-none' : ''}`}
                            >
                                <item.icon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                                <span>{item.name}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const userDropdown = (mobile = false) => {
        if (visibleUserItems.length === 0) return null;
        const anyActive = visibleUserItems.some((i) => isActive(i.href));
        return (
            <div>
                <button
                    onClick={() => setUserOpen((o) => !o)}
                    className={`flex items-center gap-3 rounded-lg px-2 w-full transition-colors text-sm ${
                        mobile ? 'h-10' : 'py-2'
                    } ${sidebarCollapsed && !mobile ? 'justify-center' : ''} ${
                        anyActive ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
                    }`}
                    title={sidebarCollapsed && !mobile ? 'Users' : undefined}
                >
                    <UsersIcon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                    {(mobile || !sidebarCollapsed) && (
                        <>
                            <span className="flex-1 text-left">Users</span>
                            <ChevronDownIcon
                                className={`h-3 w-3 flex-shrink-0 transition-transform ${userOpen ? 'rotate-180' : ''}`}
                            />
                        </>
                    )}
                </button>
                {userOpen && (mobile || !sidebarCollapsed) && (
                    <div className="ml-4 mt-0.5 flex flex-col gap-0.5">
                        {visibleUserItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={mobile ? () => setSidebarOpen(false) : undefined}
                                className={`flex items-center gap-3 rounded-lg px-2 transition-colors text-sm ${
                                    mobile ? 'h-10' : 'py-2'
                                } ${
                                    isActive(item.href) ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
                                } ${item.disabled ? 'text-gray-400 cursor-not-allowed pointer-events-none' : ''}`}
                            >
                                <item.icon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                                <span>{item.name}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const commercialDropdown = (mobile = false) => {
        if (visibleCommercialItems.length === 0) return null;
        const anyActive = visibleCommercialItems.some((i) => isActive(i.href));
        return (
            <div>
                <button
                    onClick={() => setCommercialOpen((o) => !o)}
                    className={`flex items-center gap-3 rounded-lg px-2 w-full transition-colors text-sm ${
                        mobile ? 'h-10' : 'py-2'
                    } ${sidebarCollapsed && !mobile ? 'justify-center' : ''} ${
                        anyActive ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
                    }`}
                    title={sidebarCollapsed && !mobile ? 'Commercial' : undefined}
                >
                    <BriefcaseIcon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                    {(mobile || !sidebarCollapsed) && (
                        <>
                            <span className="flex-1 text-left">Commercial</span>
                            <ChevronDownIcon
                                className={`h-3 w-3 flex-shrink-0 transition-transform ${commercialOpen ? 'rotate-180' : ''}`}
                            />
                        </>
                    )}
                </button>
                {commercialOpen && (mobile || !sidebarCollapsed) && (
                    <div className="ml-4 mt-0.5 flex flex-col gap-0.5">
                        {visibleCommercialItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={mobile ? () => setSidebarOpen(false) : undefined}
                                className={`flex items-center gap-3 rounded-lg px-2 transition-colors text-sm ${
                                    mobile ? 'h-10' : 'py-2'
                                } ${
                                    isActive(item.href) ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
                                } ${item.disabled ? 'text-gray-400 cursor-not-allowed pointer-events-none' : ''}`}
                            >
                                <item.icon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                                <span>{item.name}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const navLink = (item: NavItem, mobile = false) => (
        <Link
            key={item.name}
            to={item.href}
            aria-disabled={item.disabled}
            onClick={mobile ? () => setSidebarOpen(false) : undefined}
            className={`flex items-center gap-3 rounded-lg px-2 transition-colors text-sm ${
                mobile ? 'h-10' : 'py-2'
            } ${sidebarCollapsed && !mobile ? 'justify-center' : ''} ${
                isActive(item.href) ? 'bg-gray-400 text-primary-content' : 'hover:bg-gray-200'
            } ${item.disabled ? 'text-gray-400 cursor-not-allowed pointer-events-none' : ''}`}
            title={sidebarCollapsed && !mobile ? item.name : undefined}
        >
            <item.icon className={`flex-shrink-0 ${mobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
            {(mobile || !sidebarCollapsed) && <span>{item.name}</span>}
        </Link>
    );

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Mobile sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-10/12 bg-base-100 flex flex-col justify-between transform transition-transform duration-300 ease-in-out lg:hidden ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex flex-col">
                    <div className="flex h-16 items-center justify-between px-4 border-b border-base-300">
                        <span className="text-lg font-grotesk font-bold">
                            <span className="uppercase">Peak</span>
                            <span className="font-medium text-gray-500">^</span> Admin
                        </span>
                        <button onClick={() => setSidebarOpen(false)} className="btn btn-ghost btn-sm btn-square">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                    <nav className="flex flex-col gap-1 p-4">
                        {(() => {
                            const idx = visibleNav.findIndex((i) => i.href === '/dashboard');
                            const before = idx === -1 ? visibleNav : visibleNav.slice(0, idx + 1);
                            const after = idx === -1 ? [] : visibleNav.slice(idx + 1);
                            return (<>{before.map((item) => navLink(item, true))}{analyticsDropdown(true)}{userDropdown(true)}{commercialDropdown(true)}{canSee(employeesItem.href) && navLink(employeesItem, true)}{after.map((item) => navLink(item, true))}</>);
                        })()}
                    </nav>
                </div>
                <div className="flex flex-col gap-2 p-4 border-t">
                    <span className="truncate line-clamp-1 text-sm">{currentUser?.email}</span>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 rounded-lg px-2 h-10 w-full text-red-600 text-sm hover:bg-error/10 transition-colors"
                    >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Desktop sidebar */}
            <aside
                className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:flex-col border-r bg-white transition-all duration-300 ${
                    sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
                }`}
            >
                <div className="flex h-16 items-center justify-between px-2 border-b">
                    {!sidebarCollapsed && (
                        <span className="text-lg font-grotesk font-bold">
                            <span className="uppercase">Peak</span>
                            <span className="font-medium text-gray-500">^</span> Admin
                        </span>
                    )}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className={`btn btn-ghost btn-sm btn-square ${sidebarCollapsed ? 'mx-auto' : ''}`}
                    >
                        {sidebarCollapsed
                            ? <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                            : <ArrowLeftStartOnRectangleIcon className="h-4 w-4" />
                        }
                    </button>
                </div>
                <nav className="flex flex-col gap-1 p-2 flex-1 overflow-y-auto">
                    {(() => {
                        const idx = visibleNav.findIndex((i) => i.href === '/dashboard');
                        const before = idx === -1 ? visibleNav : visibleNav.slice(0, idx + 1);
                        const after = idx === -1 ? [] : visibleNav.slice(idx + 1);
                        return (<>{before.map((item) => navLink(item))}{analyticsDropdown()}{userDropdown()}{commercialDropdown()}{canSee(employeesItem.href) && navLink(employeesItem)}{after.map((item) => navLink(item))}</>);
                    })()}
                </nav>
                <div className="p-2 border-t">
                    <button
                        onClick={handleLogout}
                        className={`flex items-center gap-3 rounded-lg px-2 py-2 w-full text-red-600 text-sm hover:bg-error/10 transition-colors ${
                            sidebarCollapsed ? 'justify-center' : ''
                        }`}
                        title={sidebarCollapsed ? 'Logout' : undefined}
                    >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
                        {!sidebarCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main content area */}
            <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
                {/* Top navigation bar */}
                <header className="sticky top-0 z-30 h-16 bg-base-100 border-b flex items-center justify-between px-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="btn btn-ghost btn-sm btn-square lg:hidden"
                        >
                            <Bars3Icon className="h-6 w-6" />
                        </button>
                        <h1 className="text-lg font-semibold lg:hidden">Peak Admin</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/profile"
                            title={currentUser?.email ?? 'Profile'}
                            className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden bg-gray-200 hover:ring-2 hover:ring-gray-400 transition-all flex-shrink-0"
                        >
                            <UserIcon className="h-5 w-5 text-gray-600" />
                        </Link>
                    </div>
                </header>

                {/* Page content */}
                <main className="h-full flex flex-col min-h-[calc(100vh-64px)]">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default DashboardWrapper;
